/**
 * Durations for the SLA panel: a budget, an elapsed, an overrun — `3d 2h`, `45m 12s`, `18s`.
 *
 * The panel puts these in fixed columns beside each other, and a reader comparing two rows is comparing
 * magnitudes, not arithmetic. Two rules follow. The unit is always the largest the duration reaches, so
 * a three-day delta never appears as `74h 12m` — a figure the reader has to divide before they know
 * whether to care. And at most two terms appear, so the column width does not vary with how many units a
 * particular duration happens to land on.
 */

import { millisecondsToTimePeriod } from './time-helper';

const MS_IN_MINUTE = 60 * 1000;

/** How many terms are rendered. The third is always under a percent of the total. */
const MAX_TERMS = 2;

/**
 * The same rule as `format-count.ts`'s `requireFinite`, and for the same reason: `0s` is a specific,
 * false, entirely plausible-looking claim about an SLA. The panel's ErrorBoundary turns the throw into a
 * contained "failed to load", which tells the reader the truth — the number is not available.
 *
 * Negatives are rejected rather than clamped. An overrun computed the wrong way round would otherwise
 * surface as a healthy `0s` instead of as the defect it is.
 */
const requireDuration = (milliseconds: number): number => {
  if (!Number.isFinite(milliseconds)) {
    throw new Error(`Panel payload carried a non-numeric duration: ${milliseconds}`);
  }
  if (milliseconds < 0) {
    throw new Error(`Panel payload carried a negative duration: ${milliseconds}`);
  }
  return milliseconds;
};

/**
 * `3d 2h`, `1m 30s`, `0s` — the duration in at most two units, largest first.
 *
 * Terms that are zero are dropped rather than padded: `259_200_000` is `3d`, not `3d 0h`. A term saying
 * nothing costs a column its width, and a reader scanning down reads a trailing `0h` as precision that
 * is not there.
 *
 * The two terms are the two largest *populated* units, not the two adjacent to the largest, so a duration
 * with a hole in it keeps its second figure: `3d 0h 45m` is `3d 45m`, not `3d`. Dropping the zero before
 * choosing which units to render would have discarded the 45m along with it — an SLA overrun quietly
 * reported as smaller than it is.
 *
 * Truncated at every unit, never rounded, so the rendered figure never overstates the duration it stands
 * for — and a duration under a second reads `0s` rather than being promoted to a unit it never reached.
 */
export const formatDuration = (milliseconds: number): string => {
  const value = requireDuration(milliseconds);

  // `millisecondsToTimePeriod` stops at minutes, so the seconds term is derived here; everything above it
  // comes from the shared helper rather than being re-divided.
  const { days, hours, minutes } = millisecondsToTimePeriod(value);
  const seconds = Math.floor((value % MS_IN_MINUTE) / 1000);

  const terms = [
    { value: days, suffix: 'd' },
    { value: hours, suffix: 'h' },
    { value: minutes, suffix: 'm' },
    { value: seconds, suffix: 's' },
  ];

  const populated = terms.filter((term) => term.value > 0);
  if (populated.length === 0) {
    return '0s';
  }

  return populated
    .slice(0, MAX_TERMS)
    .map((term) => `${term.value}${term.suffix}`)
    .join(' ');
};
